import { Injectable } from '@angular/core';
import { UrlEnhancerService } from '@url';
import { Config } from '@config';
import { AuthHttp } from '@http';
import CustomStore from 'devextreme/data/custom_store';
import Store from 'devextreme/data/custom_store';
import { LoaderService } from '@loader';
import { __i18n } from '@localisation';
import { MessagesService, MessageType } from '@ui-messages';
import { M2MDeviceDetails } from './m2m-device-details';
import { lastValueFrom } from 'rxjs';

@Injectable()
export class M2MDataStore {
    private lastErrorMessageId: string = null;

    constructor(
        private urlEnhancerService: UrlEnhancerService,
        private config: Config,
        private http: AuthHttp,
        private loaderService: LoaderService,
        private messagesService: MessagesService) {}

    private collectNegativeFilterValues(filter: any[]): any {
        const result = {
            name: null,
            values: []
        };
        if (Array.isArray(filter[0])) {
            for (let i = 0; i < filter.length; i += 2) {
                const subRes = this.collectNegativeFilterValues(filter[i]);
                if (subRes !== null) {
                    result.name = subRes.name;
                    result.values = result.values.concat(subRes.values);
                }
            }
        } else if (filter.length === 3) {
            result.name = filter[0];
            result.values.push(filter[2]);
        }
        return result.name ? result : null;
    }

    private parseFilters(availableHeaderFilterValues: any, filterArr: any[]): string {
        let result: string = '';
        if (Array.isArray(filterArr[0])) {
            for (let i = 0; i < filterArr.length; i += 2) {
                result += this.parseFilters(availableHeaderFilterValues, filterArr[i]);
            }
        } else if (filterArr.length === 3) {
            const values = filterArr[2].split(/\s*[,;\s]\s*/).filter((v) => !!v);
            switch (filterArr[0]) {
                case 'iccid':
                case 'imsi':
                    for (let j = 0; j < values.length; j++) {
                        result += '&' + filterArr[0] + 's%5B%5D=' +
                            encodeURIComponent(values[j].replace(/^\s+/, '').replace(/\s+$/, ''));
                    }
                    break;

                case 'state':
                    for (let j = 0; j < values.length; j++) {
                        result += '&deviceStates%5B%5D=' +
                            encodeURIComponent(values[j].replace(/^\s+/, '').replace(/\s+$/, ''));
                    }
                    break;
            }
        } else if (filterArr.length === 2 && filterArr[0] === '!' && Array.isArray(filterArr[1])) {
            const negFilter = this.collectNegativeFilterValues(filterArr[1]);
            if (negFilter !== null) {
                if (availableHeaderFilterValues && Array.isArray(availableHeaderFilterValues[negFilter.name])) {
                    const posValues = availableHeaderFilterValues[negFilter.name].filter((el) => {
                        return negFilter.values.indexOf(el.value) < 0;
                    });
                    if (posValues.length > 0) {
                        for (let j = 0; j < posValues.length; j++) {
                            result += '&deviceStates%5B%5D=' +
                                encodeURIComponent(posValues[j].value.replace(/^\s+/, '').replace(/\s+$/, ''));
                        }
                    }
                }
            }
        }
        return result;
    }

    public getStore(config: any): Store {
        const url = this.urlEnhancerService.replaceParams(this.config.services.tenant.m2mDevices);
        return new CustomStore({
            key: 'iccid',
            load: (loadOptions: any) => {
                let params = '?';

                params += 'pageOffset=';
                params += loadOptions.skip || 0;
                params += '&pageSize=';
                params += loadOptions.take || 20;
                if(loadOptions.sort && loadOptions.sort.length === 1) {
                    const sort = loadOptions.sort[0];

                    params += '&sortField=';
                    params += sort.selector;
                    params += '&sortOrder=';
                    params += sort.desc ? 'descending' : 'ascending';
                }
                if (loadOptions.filter && loadOptions.filter.length) {
                    params += this.parseFilters(config ? config.availableHeaderFilterValues : null, loadOptions.filter);
                }

                const loaderId = this.loaderService.start();
                this.loaderService.setText(__i18n('loading data', 'm2mrouter.loader.text.loadingdata'));
                return new Promise((resolve, reject) => {
                    if (this.lastErrorMessageId !== null) {
                        this.messagesService.deleteMessage(this.lastErrorMessageId);
                        this.lastErrorMessageId = null;
                    }
                    lastValueFrom(this.http.getJSON(url + params))
                    .then((data: any) => {
                        resolve({
                            data: data.items,
                            totalCount: data.totalCount
                        });
                    })
                    .catch((error: any) => {
                        this.lastErrorMessageId = this.messagesService.addMessage({
                            type: MessageType.error,
                            text: error && error.error && error.error.error &&
                                typeof(error.error.error.errorText === 'string')
                                ? error.error.error.errorText.replace('The request is invalid: ', '')
                                : 'Failed to load data.'
                        });

                        resolve({
                            data: [],
                            totalCount: 0
                        });
                    })
                    .finally(() => {
                        this.loaderService.stop(loaderId);
                    });
                });
            }
        });
    }

    public async getDeviceDetails(deviceId: string): Promise<M2MDeviceDetails> {
        const url = this.urlEnhancerService.replaceParams(
            this.config.services.tenant.m2mDeviceDetail, {deviceid: deviceId});

        const loaderId = this.loaderService.start();
        this.loaderService.setText(__i18n('loading data', 'm2mrouter.details.loader.text.loadingdata'));
        return new Promise((resolve, reject) => {
            if (this.lastErrorMessageId !== null) {
                this.messagesService.deleteMessage(this.lastErrorMessageId);
                this.lastErrorMessageId = null;
            }
            lastValueFrom(this.http.getJSON(url))
                .then((data: any) => {
                    resolve(data);
                })
                .catch((error: any) => {
                    if (error.status === 404) {
                        reject(error.status);
                        return;
                    }
                    this.lastErrorMessageId = this.messagesService.addMessage({
                        type: MessageType.error,
                        text: error && error.error && error.error.error &&
                            typeof(error.error.error.errorText === 'string')
                            ? error.error.error.errorText.replace('The request is invalid: ', '')
                            : 'Failed to load data.'
                    });

                    resolve(null);
                })
                .finally(() => {
                    this.loaderService.stop(loaderId);
                });
        });
}
}
